/* eslint-disable camelcase */
import {
  App, Button, Drawer, Flex, Form, Input, Skeleton,
} from 'antd';
import { fetchCustomers } from 'api/customers';
import FormItem from 'components/FormItem';
import FormLayout from 'components/FormLayout';
import SourceInput from 'components/SourceInput';
import { EMPTY_OBJECT } from 'constants';
import useLoadings from 'hooks/useLoadings';
import React, { useEffect, useRef } from 'react';
import PhoneInput from 'react-phone-input-2';
import { captureException } from 'utils/errors';
import { getHttpErrorMessage } from 'utils/http';

function ContactDrawerForm({
  data = EMPTY_OBJECT,
  loading,
  open,
  onClose,
  onSubmit,
  onDelete,
}) {
  return (
    <Drawer
      title={`${data?.id ? 'Edit' : 'Add'} contact`}
      placement="right"
      onClose={onClose}
      open={open}
      size="large"
      styles={{ body: { padding: 0, paddingBottom: 64 } }}
    >
      {loading ? (
        <FormLayout.Section divider={false}>
          <Skeleton paragraph={{ rows: 10 }} />
        </FormLayout.Section>
      ) : (
        <ContactForm
          data={data}
          onSubmit={onSubmit}
          onDelete={onDelete}
          onClose={onClose}
          open={open}
        />

      )}
    </Drawer>
  );
}

function ContactForm({
  data, onSubmit, onDelete, onClose, open,
}) {
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const customerDataTouchedRef = useRef(false);
  const { setLoading, isLoading } = useLoadings();

  const isEditMode = Boolean(data?.id);

  useEffect(() => {
    if (!data) {
      return;
    }
    const payload = { ...data };
    if (!payload.phone) {
      delete payload.phone;
    }
    form.setFieldsValue(payload);
  }, [data, form]);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      customerDataTouchedRef.current = false;
    }
  }, [open]);

  const handleSubmit = async (data) => {
    setLoading('submit', true);
    return onSubmit(serialize(data))
      .then((res) => {
        message.success(`Contact ${isEditMode ? 'updated' : 'created'} successfully!`);
        onClose();
      })
      .catch((e) => {
        message.error(getHttpErrorMessage(e, false));
        captureException(e);
      })
      .finally(() => {
        setLoading('submit', false);
      });
  };

  const searchCustomer = (value) => {
    setLoading('searchCustomer', true);
    fetchCustomers({ searchKey: value })
      .then((res) => {
        if (res.data.results.length) {
          const customer = res.data.results.find((c) => c.email) || res.data.results[0];
          // If customer only has 'name', extract first_name and last_name
          if (customer.name) {
            const [first_name, ...rest] = customer.name.split(' ');
            customer.first_name = first_name;
            customer.last_name = rest.join(' ');
          }
          form.setFieldValue('first_name', customer.first_name);
          form.setFieldValue('last_name', customer.last_name);
          form.setFieldValue('email', customer.email);
          customerDataTouchedRef.current = false;
        } else {
          form.resetFields();
          form.setFieldValue('phone', value);
        }
      })
      .catch((e) => {
        captureException(e);
      })
      .finally(() => setLoading('searchCustomer', false));
  };
  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      style={{ marginTop: 24 }}
      onValuesChange={(changedValues, allValues) => {
        if (changedValues.first_name || changedValues.last_name || changedValues.email) {
          customerDataTouchedRef.current = true;
        }
      }}
    >
      <FormLayout.Section divider={false}>
        <FormItem name="id" hidden />
        <FormItem
          name="phone"
          label="Phone number"
        >
          <PhoneInput
            country="us"
            buttonClass="phone-country"
            inputClass="input"
            inputProps={{
              name: 'phone',
              required: true,
              autoFocus: true,
            }}
            onChange={(value, data, event, formattedValue) => {
              // Check if the phone number is complete based on the country format
              const isComplete = formattedValue.length >= data.format.length;
              if (isComplete) {
                if (!customerDataTouchedRef.current && !isEditMode) {
                  searchCustomer(value);
                }
              }
            }}
            inputStyle={{ width: '100%' }}
            placeholder="Enter phone number"
            autofocus
            preferredCountries={['us', 'ca']}
            enableSearch
            regions={['america', 'europe']}
            countryCodeEditable={false}
            masks={{
              pr: '(...) ...-....',
            }}
            disabled={isLoading()}
          />
        </FormItem>
        <>
          <Flex gap={16} wrap>
            <FormItem
              name="first_name"
              label="First name"
            >
              <Input
                disabled={isLoading()}
              />
            </FormItem>
            <FormItem
              name="last_name"
              label="Last name"
            >
              <Input
                disabled={isLoading()}
              />
            </FormItem>
            <FormItem
              name="email"
              label="Email"
            >
              <Input disabled={isLoading()} />
            </FormItem>
            <FormItem
              name="source"
              label="Origin"
            >
              <SourceInput disabled={isLoading()} />
            </FormItem>
          </Flex>

          <FormItem name="role" label="Role">
            <Input disabled={isLoading()} />
          </FormItem>
        </>

      </FormLayout.Section>
      <FormLayout.Footer>
        <Flex justify={isEditMode ? 'space-between' : 'flex-end'} className="w-full">
          {isEditMode && onDelete && (
            <Button danger disabled={isLoading()} onClick={onDelete}>
              Delete
            </Button>
          )}
          <Flex gap={4}>
            <Button disabled={isLoading()} onClick={onClose}>
              Cancel
            </Button>
            <Button
              htmlType="submit"
              type="primary"
              disabled={isLoading()}
              loading={isLoading('submit')}
            >
              {isEditMode ? 'Save' : 'Add'}
            </Button>
          </Flex>
        </Flex>
      </FormLayout.Footer>
    </Form>
  );
}

function serialize(data) {
  return {
    ...data,
    phone: data.phone ? `+${data.phone.replace('+', '')}` : null,
  };
}

export default ContactDrawerForm;
